/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useMemo } from 'react';
import { useAppConfig, useLocale } from '@airasia-rn/ui';
import AccountEnvironmentURLs, {
  AccountEndpoints,
  parseEnv,
} from '../config/EnvironmentURLs';

export const useAccountUrl = (endpoint: AccountEndpoints): string => {
  const { config } = useAppConfig();
  return AccountEnvironmentURLs[endpoint][parseEnv(config.env)];
};

const useAboutUsLegalUrl = (path: string): string => {
  // Host follows app env (stg/prod); path uses current locale.
  const baseAccountUrl = useAccountUrl(AccountEndpoints.BASE_ACCOUNT_URL);
  const { currentLanguage } = useLocale();
  const localePath = currentLanguage.replace('-', '/');

  return `${baseAccountUrl}/aa/about-us/${localePath}/${path}`;
};

export const useHelpCenterUrl = (): string => {
  const faqBaseUrl = useAccountUrl(AccountEndpoints.FAQ_BASE_URL);
  const { currentLanguage } = useLocale();

  // Salesforce Community expects codes like en_GB / ms / in (not app locales like ms-my).
  const helpLanguages = useMemo<Record<string, string>>(
    () => ({
      'en-gb': 'en_GB',
      'ms-my': 'ms',
      'id-id': 'in',
      'th-th': 'th',
      'vi-vn': 'vi',
      'zh-tw': 'zh_TW',
      'zh-hk': 'zh_TW',
      'zh-cn': 'zh_CN',
      'ja-jp': 'ja',
      'ko-kr': 'ko',
      'tr-tr': 'tr',
      'km-kh': 'km',
    }),
    [],
  );
  const language = helpLanguages[currentLanguage.toLowerCase()] ?? 'en_GB';

  return `${faqBaseUrl}?language=${encodeURIComponent(language)}`;
};

export const useTermsAndConditionsUrl = (): string =>
  useAboutUsLegalUrl('terms-and-conditions.html');

export const useAccountClosureTermsAndConditionsUrl = (): string =>
  useAboutUsLegalUrl('closure-of-membership-account-terms-and-conditions.html');

export const useTermsOfUseUrl = (): string =>
  useAboutUsLegalUrl('terms-of-use.html');

export const usePrivacyStatementUrl = (): string =>
  useAboutUsLegalUrl('privacy-statement.html');

export const useMyCasesWidgetScriptUrl = (): string =>
  useAccountUrl(AccountEndpoints.MY_CASES_WIDGET_SCRIPT_URL);
